import { useEffect, useState, useCallback } from "react";
import api from "../../api/client";

const BADGE = {
  aprobado:    { bg: "bg-green-100",  text: "text-green-700",  label: "Aprobado"    },
  pendiente:   { bg: "bg-amber-100",  text: "text-amber-700",  label: "Pendiente"   },
  rechazado:   { bg: "bg-red-100",    text: "text-red-700",    label: "Rechazado"   },
  otp_enviado: { bg: "bg-blue-100",   text: "text-blue-700",   label: "En proceso"  },
  error:       { bg: "bg-gray-100",   text: "text-gray-500",   label: "Error"       },
};

function EstadoBadge({ estado }) {
  const b = BADGE[estado] || { bg: "bg-gray-100", text: "text-gray-500", label: estado };
  return (
    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${b.bg} ${b.text}`}>
      {b.label}
    </span>
  );
}

function fmtFecha(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-VE", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function fmtMonto(monto, moneda) {
  return `${Number(monto).toLocaleString("es-VE", { minimumFractionDigits: 2 })} ${moneda}`;
}

export default function HistorialPagos() {
  const [cobros, setCobros]   = useState([]);
  const [uso, setUso]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const cargar = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/pagos/cobros"),
      api.get("/suscripcion/"),
    ])
      .then(([r1, r2]) => {
        setCobros(r1.data || []);
        setUso(r2.data);
      })
      .catch(() => setError("No se pudo cargar el historial de pagos"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const totalAprobado = cobros
    .filter((c) => c.estado === "aprobado")
    .reduce((sum, c) => sum + Number(c.monto), 0);

  const moneda = cobros[0]?.moneda || "VES";

  if (loading) return (
    <div className="flex items-center justify-center h-40">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">{error}</div>
  );

  return (
    <div className="max-w-4xl space-y-6">

      {/* Encabezado */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Historial de pagos</h2>
        <p className="text-gray-500 text-sm mt-1">Registro de todos tus cobros de suscripción</p>
      </div>

      {/* Tarjetas resumen */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Plan actual</p>
          <p className="text-xl font-bold text-indigo-600 capitalize">{uso?.plan || "—"}</p>
          {uso?.fecha_fin && (
            <p className="text-xs text-gray-400 mt-1">Vence {fmtFecha(uso.fecha_fin)}</p>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Total pagado</p>
          <p className="text-xl font-bold text-gray-800">
            {totalAprobado.toLocaleString("es-VE", { minimumFractionDigits: 2 })} {moneda}
          </p>
          <p className="text-xs text-gray-400 mt-1">{cobros.filter(c => c.estado === "aprobado").length} pago(s) aprobado(s)</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Último pago</p>
          {cobros.length > 0 ? (
            <>
              <p className="text-xl font-bold text-gray-800">{fmtFecha(cobros[0].iniciado_en)}</p>
              <EstadoBadge estado={cobros[0].estado} />
            </>
          ) : (
            <p className="text-xl font-bold text-gray-400">Sin pagos</p>
          )}
        </div>
      </div>

      {/* Tabla de movimientos */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">Movimientos</h3>
          <button
            onClick={cargar}
            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
          >
            ↻ Actualizar
          </button>
        </div>

        {cobros.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <p className="text-4xl mb-3">💳</p>
            <p className="text-gray-500 text-sm">No hay pagos registrados aún.</p>
            <p className="text-gray-400 text-xs mt-1">Aquí aparecerán tus cobros de suscripción.</p>
          </div>
        ) : (
          <>
            {/* Cabecera tabla — solo desktop */}
            <div className="hidden sm:grid grid-cols-12 gap-4 px-5 py-2.5 bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-400 border-b border-gray-100">
              <div className="col-span-5">Concepto</div>
              <div className="col-span-2">Fecha</div>
              <div className="col-span-2 text-right">Monto</div>
              <div className="col-span-3 text-right">Estado</div>
            </div>

            <div className="divide-y divide-gray-100">
              {cobros.map((c) => (
                <div
                  key={c.id}
                  className="grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
                >
                  {/* Concepto */}
                  <div className="sm:col-span-5">
                    <p className="text-sm font-medium text-gray-800">{c.concepto}</p>
                    {c.resuelto_en && (
                      <p className="text-xs text-gray-400 mt-0.5 sm:hidden">
                        Resuelto: {fmtFecha(c.resuelto_en)}
                      </p>
                    )}
                  </div>

                  {/* Fecha iniciado */}
                  <div className="sm:col-span-2 flex sm:block items-center gap-2">
                    <span className="text-xs text-gray-400 sm:hidden">Fecha:</span>
                    <p className="text-sm text-gray-600">{fmtFecha(c.iniciado_en)}</p>
                    {c.resuelto_en && (
                      <p className="text-xs text-gray-400 hidden sm:block mt-0.5">
                        → {fmtFecha(c.resuelto_en)}
                      </p>
                    )}
                  </div>

                  {/* Monto */}
                  <div className="sm:col-span-2 flex sm:block items-center gap-2 sm:text-right">
                    <span className="text-xs text-gray-400 sm:hidden">Monto:</span>
                    <p className="text-sm font-semibold text-gray-800">{fmtMonto(c.monto, c.moneda)}</p>
                  </div>

                  {/* Estado */}
                  <div className="sm:col-span-3 flex items-center sm:justify-end gap-2">
                    <span className="text-xs text-gray-400 sm:hidden">Estado:</span>
                    <EstadoBadge estado={c.estado} />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <p className="text-center text-xs text-gray-400">
        🔒 Los pagos se procesan por débito inmediato SyPago. Tus datos bancarios están cifrados.
      </p>
    </div>
  );
}
